import dayjs from 'dayjs';
import cn from 'classnames';
import { isEmpty } from 'lodash';
import PropTypes from 'prop-types';
import React, { Fragment, useEffect } from 'react';
import InputMask from 'react-input-mask';
import DatePicker from 'react-datepicker';
import { useOnClickOutside } from 'helper';
import { BiCalendarAlt } from 'react-icons/bi';
import { Popover, Transition } from '@headlessui/react';
// import { ExclamationCircleIcon } from '@heroicons/react/outline';
import { useInputId } from './hooks';

const formatDate = (value, defaultFormat = 'YYYY-MM-DD') => {
  const date = dayjs(new Date(value));
  return date.isValid() ? date.format(defaultFormat) : value;
};

const formatInputMaskDate = (value, defaultFormat = 'DD/MM/YYYY') => {
  const date = dayjs(new Date(value));
  return date.isValid() ? date.format(defaultFormat) : '';
};

const parseValue = (value, defaultValue = '') => {
  const date = dayjs(new Date(value));
  return date.isValid() ? date.toDate() : defaultValue;
};

const isDateValid = (value, orEmpty = false) => {
  if ((isEmpty(value) || value === '__/__/____') && orEmpty) return true;
  const date = dayjs(new Date(value));
  return date.isValid();
};

function FormDate({
  id: defaultId,
  icon: Icon,
  label,
  name,
  onChange,
  onSetFieldValue,
  value,
  error,
  required,
  panelClassName,
  maxDate,
  minDate,
  ...props
}) {
  const [val, setVal] = React.useState(formatInputMaskDate(value));
  const [id] = useInputId(defaultId);
  const handleChange = ({ target }) => {
    setVal(target.value);

    const parsedDate = target.value?.split('/')?.reverse()?.join('/');

    if (target.value === '__/__/____') {
      if (typeof onChange === 'function') {
        onChange((state) => ({
          ...state,
          [name]: '',
        }));
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, '');
      }
    }
    if (isDateValid(parsedDate)) {
      if (typeof onChange === 'function') {
        onChange((state) => ({
          ...state,
          [name]: formatDate(parsedDate),
        }));
      }
      if (typeof onSetFieldValue === 'function') {
        onSetFieldValue(name, formatDate(parsedDate));
      }
    }
  };
  const showIcon = Icon || error;
  const isError = !!error;
  const [showPicker, setShowPicker] = React.useState(false);
  const myRef = React.useRef();
  useOnClickOutside(myRef, setShowPicker);
  const handleToggle = () => {
    setShowPicker(true);
  };
  const defaultDate = React.useMemo(() => parseValue(value), [value]);
  const handleSetDate = (v) => {
    const newDate = dayjs(v);
    if (typeof onChange === 'function') {
      onChange((state) => ({
        ...state,
        [name]: newDate.format('YYYY-MM-DD'),
      }));
    }
    if (typeof onSetFieldValue === 'function') {
      onSetFieldValue(name, newDate.format('YYYY-MM-DD'));
    }
    setVal(newDate.format('DD/MM/YYYY'));
    setShowPicker(false);
  };

  useEffect(() => {
    setVal(formatInputMaskDate(value));
  }, [value]);

  return (
    <Popover className="relative z-[2]">
      <div className="flex flex-col">
        <label htmlFor={id} className="block font-medium mb-2 text-gray-500">
          <span>{label}</span>
          {required && <span className="ml-1 font-light text-red-500">*</span>}
        </label>
        <div
          ref={myRef}
          className={cn(
            'relative border rounded-2xl bg-white p-3 shadow-sm focus-within:ring-1 z-[1]',
            isError
              ? 'border-red-300 focus-within:ring-red-500 focus-within:border-red-500 animate-wiggle'
              : 'border-gray-300 focus-within:ring-primary-600 focus-within:border-primary-600'
          )}
        >
          <InputMask
            mask="99/99/9999"
            id={id}
            name="name"
            className={cn(
              'block w-full border-0 p-0 text-gray-900 placeholder-gray-300 focus:ring-0 text-base',
              'focus:outline-none',
              showIcon ? 'pr-10' : ''
            )}
            onChange={handleChange}
            value={val}
            {...props}
          />
          {Icon && (
            <div
              className="absolute inset-y-0 right-0 pr-3 flex items-center z-10"
              title={error || ''}
            >
              <button
                className="border-none bg-transparent text-gray-400 focus:outline-none focus:text-primary-500"
                type="button"
                onClick={handleToggle}
              >
                <Icon className="h-5 w-5" aria-hidden="true" />
              </button>
            </div>
          )}
          <Transition
            show={showPicker}
            enter="transition duration-100 ease-out"
            enterFrom="transform scale-95 opacity-0"
            enterTo="transform scale-100 opacity-100"
            leave="transition duration-75 ease-out"
            leaveFrom="transform scale-100 opacity-100"
            leaveTo="transform scale-95 opacity-0"
            as={Fragment}
          >
            <Popover.Panel
              static
              className={cn('absolute z-[60]', panelClassName)}
            >
              <div className="border rounded-lg overflow-hidden shadow-lg">
                <DatePicker
                  selected={defaultDate}
                  onChange={handleSetDate}
                  inline
                  disabledKeyboardNavigation
                  maxDate={maxDate}
                  minDate={minDate}
                />
              </div>
            </Popover.Panel>
          </Transition>
        </div>
      </div>
    </Popover>
  );
}

FormDate.defaultProps = {
  id: '',
  icon: BiCalendarAlt,
  error: null,
  required: false,
  panelClassName: 'right-0',
  onChange: false,
  onSetFieldValue: false,
  maxDate: null,
  minDate: null,
};

FormDate.propTypes = {
  id: PropTypes.string,
  required: PropTypes.bool,
  panelClassName: PropTypes.string,
  name: PropTypes.string.isRequired,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  icon: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.bool]),
  error: PropTypes.oneOfType([PropTypes.instanceOf(Object), PropTypes.string]),
  onChange: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  onSetFieldValue: PropTypes.oneOfType([PropTypes.bool, PropTypes.func]),
  maxDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minDate: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

export default React.memo(FormDate);
